/**
 * Patient Diagnosis API Service
 * Handles all API calls for Patient Diagnosis module
 *
 * @author CareX Frontend Team
 */

import apiClient from './apiClient';
import {
  PatientDiagnosisDTO,
  CreatePatientDiagnosisRequest,
  ExamVitalsDTO,
  AmbulatoryStatusDTO,
  DiagnosesTreatmentsDTO,
  PhysicalHealthAssessmentDTO,
  MentalConditionAssessmentDTO,
  CapacityMedicationAssessmentDTO,
  HospicePhysicianInfoDTO,
  DiagnosisStatus,
  PageResponse,
} from '../types/diagnosis.types';

const BASE_PATH = '/v1/patient-diagnosis';

/**
 * Patient Diagnosis Service
 * Provides type-safe API calls with proper error handling
 */
class PatientDiagnosisService {
  // ========== Create Operations ==========

  /**
   * Create a new patient diagnosis (DRAFT status)
   * @param request - Create request with resident ID and diagnosis date
   * @returns Created diagnosis DTO
   */
  async createDiagnosis(
    request: CreatePatientDiagnosisRequest
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.post<PatientDiagnosisDTO>(
      BASE_PATH,
      request
    );
    return response.data;
  }

  // ========== Tab Update Operations (7 methods) ==========

  /**
   * Update Exam & Vitals (Tab 1)
   */
  async updateExamVitals(
    diagnosisId: string,
    data: ExamVitalsDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/exam-vitals`,
      data
    );
    return response.data;
  }

  /**
   * Update Ambulatory Status (Tab 2)
   */
  async updateAmbulatoryStatus(
    diagnosisId: string,
    data: AmbulatoryStatusDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/ambulatory-status`,
      data
    );
    return response.data;
  }

  /**
   * Update Diagnoses & Treatments (Tab 3)
   */
  async updateDiagnosesTreatments(
    diagnosisId: string,
    data: DiagnosesTreatmentsDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/diagnoses-treatments`,
      data
    );
    return response.data;
  }

  /**
   * Update Physical Health Assessment (Tab 4)
   */
  async updatePhysicalHealthAssessment(
    diagnosisId: string,
    data: PhysicalHealthAssessmentDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/physical-health`,
      data
    );
    return response.data;
  }

  /**
   * Update Mental Condition Assessment (Tab 5)
   */
  async updateMentalConditionAssessment(
    diagnosisId: string,
    data: MentalConditionAssessmentDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/mental-condition`,
      data
    );
    return response.data;
  }

  /**
   * Update Capacity & Medication Assessment (Tab 6)
   */
  async updateCapacityMedicationAssessment(
    diagnosisId: string,
    data: CapacityMedicationAssessmentDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/capacity-medication`,
      data
    );
    return response.data;
  }

  /**
   * Update Hospice & Physician Info (Tab 7)
   */
  async updateHospicePhysicianInfo(
    diagnosisId: string,
    data: HospicePhysicianInfoDTO
  ): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.patch<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/hospice-physician`,
      data
    );
    return response.data;
  }

  // ========== Read Operations ==========

  /**
   * Get diagnosis by ID
   */
  async getDiagnosisById(id: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.get<PatientDiagnosisDTO>(
      `${BASE_PATH}/${id}`
    );
    return response.data;
  }

  /**
   * Get all diagnoses for a resident
   */
  async getDiagnosesByResident(
    residentId: string
  ): Promise<PatientDiagnosisDTO[]> {
    const response = await apiClient.get<PatientDiagnosisDTO[]>(
      `${BASE_PATH}/resident/${residentId}`
    );
    return response.data;
  }

  /**
   * Get latest diagnosis for a resident
   */
  async getLatestDiagnosis(residentId: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.get<PatientDiagnosisDTO>(
      `${BASE_PATH}/resident/${residentId}/latest`
    );
    return response.data;
  }

  /**
   * Get diagnoses by status (paginated)
   */
  async getDiagnosesByStatus(
    status: DiagnosisStatus,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<PatientDiagnosisDTO>> {
    const response = await apiClient.get<PageResponse<PatientDiagnosisDTO>>(
      `${BASE_PATH}/status/${status}`,
      {
        params: { page, size },
      }
    );
    return response.data;
  }

  /**
   * Get diagnoses by facility (paginated)
   */
  async getDiagnosesByFacility(
    facilityId: string,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<PatientDiagnosisDTO>> {
    const response = await apiClient.get<PageResponse<PatientDiagnosisDTO>>(
      `${BASE_PATH}/facility/${facilityId}`,
      {
        params: { page, size },
      }
    );
    return response.data;
  }

  /**
   * Get diagnoses pending review
   */
  async getPendingReviewDiagnoses(
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<PatientDiagnosisDTO>> {
    const response = await apiClient.get<PageResponse<PatientDiagnosisDTO>>(
      `${BASE_PATH}/pending-review`,
      {
        params: { page, size },
      }
    );
    return response.data;
  }

  // ========== Workflow Operations ==========

  /**
   * Submit diagnosis for review
   * Marks diagnosis as COMPLETED and validates required fields
   */
  async submitDiagnosis(diagnosisId: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.put<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/submit`
    );
    return response.data;
  }

  /**
   * Review and approve diagnosis
   * Requires admin role
   */
  async reviewDiagnosis(diagnosisId: string): Promise<PatientDiagnosisDTO> {
    const response = await apiClient.put<PatientDiagnosisDTO>(
      `${BASE_PATH}/${diagnosisId}/review`
    );
    return response.data;
  }

  // ========== Delete Operations ==========

  /**
   * Delete diagnosis (soft delete)
   */
  async deleteDiagnosis(diagnosisId: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${diagnosisId}`);
  }

  // ========== Helper Methods ==========

  /**
   * Get or create draft diagnosis for a resident
   * Useful for auto-save functionality
   */
  async getOrCreateDraft(
    residentId: string
  ): Promise<PatientDiagnosisDTO> {
    try {
      // Try to get existing diagnoses
      const diagnoses = await this.getDiagnosesByResident(residentId);

      // Find draft or in-progress diagnosis
      const draft = diagnoses.find(
        (d) =>
          d.status === DiagnosisStatus.DRAFT ||
          d.status === DiagnosisStatus.IN_PROGRESS
      );

      if (draft) {
        return draft;
      }

      // No draft found, create new one
      return await this.createDiagnosis({
        residentId,
        diagnosisDate: new Date().toISOString().split('T')[0], // Today's date
      });
    } catch (error: any) {
      // If no diagnoses found (404), create new draft
      if (error.response?.status === 404) {
        return await this.createDiagnosis({
          residentId,
          diagnosisDate: new Date().toISOString().split('T')[0],
        });
      }
      throw error;
    }
  }

  /**
   * Check if diagnosis is editable
   */
  isEditable(diagnosis: PatientDiagnosisDTO): boolean {
    return (
      diagnosis.status === DiagnosisStatus.DRAFT ||
      diagnosis.status === DiagnosisStatus.IN_PROGRESS
    );
  }

  /**
   * Get completion status message
   */
  getCompletionMessage(completionPercentage: number): string {
    if (completionPercentage === 0) return 'Not started';
    if (completionPercentage < 30) return 'Just started';
    if (completionPercentage < 70) return 'In progress';
    if (completionPercentage < 100) return 'Almost complete';
    return 'Complete';
  }
}

// Export singleton instance
export default new PatientDiagnosisService();
